import { ChangeEvent, FC } from 'react';
import MuiSwitch from '@mui/material/Switch';
import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';
import classNames from 'classnames';
import { FormInputSize } from '..';

interface Props {
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  disabled?: boolean;
  readonly?: boolean;
  size?: FormInputSize;
}

const useStyles = createUseStyles((theme: Theme) => ({
  switch: {
    width: 42,
    height: 26,
    padding: 0,
    '&.small': {
      width: 30,
      height: 18,

      '& .MuiSwitch-switchBase': {
        '& .MuiSwitch-thumb': {
          width: 14,
          height: 14,
        },
      },
      '& .Mui-checked': {
        transform: 'translateX(12px)',
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '100ms',
      '&.Mui-checked': {
        transform: 'translateX(14px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.success.main,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[500],
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  },
}));

const BaseSwitch: FC<Props> = ({ checked, onChange, disabled, readonly, size = 'medium' }) => {
  const styles = useStyles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event, !!event.target.checked);
  };

  return (
    <MuiSwitch
      checked={checked}
      onChange={handleChange}
      inputProps={{ 'aria-label': 'controlled' }}
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      className={classNames(styles.switch, size)}
      disabled={disabled || readonly}
    />
  );
};

export default BaseSwitch;
